import React, { useContext, useState, useCallback, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import Badge from "@material-ui/core/Badge";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import AddIcon from "@material-ui/icons/AddRounded";
import ChatIcon from "@material-ui/icons/ChatBubbleOutlineRounded";
import DeleteIcon from "@material-ui/icons/DeleteOutlineRounded";
import ChevronIcon from "@material-ui/icons/KeyboardArrowDownRounded";
import StartIcon from "@material-ui/icons/ExploreOutlined";
import WalletIcon from "@material-ui/icons/AccountBalanceWalletOutlined";
import ProjectSwitcher from "./ProjectSwitcher";
import WebPhoneChip from "../components/WebPhone/WebPhoneChip";
import PhoneIcon from "@material-ui/icons/PhoneOutlined";
import AppsIcon from "@material-ui/icons/AccountTreeOutlined";
import ConnectorsIcon from "@material-ui/icons/ExtensionOutlined";
import DataIcon from "@material-ui/icons/StorageOutlined";
import DatasetsIcon from "@material-ui/icons/TableChartOutlined";
import CollectorsIcon from "@material-ui/icons/SyncOutlined";
import JobsIcon from "@material-ui/icons/CodeOutlined";
import VaultIcon from "@material-ui/icons/VpnKeyOutlined";
import ExtensionsIcon from "@material-ui/icons/DialpadOutlined";
import StorageIcon from "@material-ui/icons/CloudOutlined";
import DevelopersIcon from "@material-ui/icons/CodeOutlined";
import AgentsIcon from "@material-ui/icons/RecordVoiceOverOutlined";
import ScheduleIcon from "@material-ui/icons/ScheduleOutlined";
import EventsIcon from "@material-ui/icons/FlashOnOutlined";
import AlertsIcon from "@material-ui/icons/ReportProblemOutlined";
import TelephonyIcon from "@material-ui/icons/SettingsPhoneOutlined";
import BuilderIcon from "@material-ui/icons/WidgetsOutlined";
import IntegrationsIcon from "@material-ui/icons/DeviceHubOutlined";
import ExitIcon from "@material-ui/icons/ExitToApp";
import SettingsIcon from "@material-ui/icons/SettingsOutlined";
import PlansIcon from "@material-ui/icons/CardMembershipOutlined";
import BillingIcon from "@material-ui/icons/ReceiptOutlined";
import AdminIcon from "@material-ui/icons/SecurityOutlined";

import { AuthContext } from "../context/Auth/AuthContext";
import { useProject } from "../context/Project/ProjectContext";
import { useConfirm } from "../components/ConfirmDialog";
import api from "../services/api";
import { connectorHealth } from "../pages/Connectors/connectorHealth";
import toastError from "../errors/toastError";
import { i18n } from "../translate/i18n";
import logoFull from "../assets/logo-numera-light.svg";
import logoMark from "../assets/logo-numera-mark.svg";

// design tokens — shell CLARO (referência BR DID). Verde = SÓ acento (marcador +
// pílula ativa suave); nunca texto. Botão primário segue charcoal (regra do tema).
const T = {
  bg: "#F6F7F9",          // fundo do conteúdo
  canvas: "#F4F4F5",      // canvas cinza-claro atrás dos painéis flutuantes
  sidebar: "#FFFFFF",     // sidebar clara
  line: "#E6E9ED",
  ink: "#1B222A",         // texto forte
  ink2: "#5B6470",        // texto secundário (itens inativos)
  ink3: "#929AA4",        // ícones inativos / terciário
  hover: "#F4F6F8",
  activeBg: "#EFF8D6",    // preenchimento da pílula ativa (verde-claro)
  activeAccent: "#86C240", // acento verde na BORDA ESQUERDA da pílula ativa (estilo ref)
  activeMark: "#BBFF00",  // acento lima (mark da marca)
  chip: "#EEF0F3",
};

const GROUPS_KEY = "nb_groups_closed";
const RAIL_OPEN = 248;
const RAIL_COLLAPSED = 52;

const MENU_FONT = 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

const useStyles = makeStyles(() => ({
  root: { display: "flex", height: "100vh", width: "100%", background: T.canvas },

  // ---- sessão de visualização (backoffice vendo como o cliente) ----
  // a faixa empurra o app para baixo em vez de flutuar sobre ele: sobrepor
  // esconderia justamente o topo da tela que se quer inspecionar
  rootViewing: { height: "calc(100vh - 34px)", marginTop: 34 },
  viewingBar: {
    position: "fixed", top: 0, left: 0, right: 0, height: 34, zIndex: 1300,
    background: "#8A2E2E", color: "#fff",
    display: "flex", alignItems: "center", justifyContent: "center", gap: 14,
    fontSize: 12.5, fontWeight: 500, letterSpacing: ".01em",
  },
  viewingText: { opacity: 0.95 },
  viewingExit: {
    font: "inherit", fontWeight: 600, color: "#8A2E2E", background: "#fff",
    border: "none", borderRadius: 6, padding: "3px 10px", cursor: "pointer",
    "&:hover": { background: "#F1E4E4" },
  },

  // ---- rail claro FLUTUANTE (card arredondado com folga; recolhível) ----
  rail: {
    width: RAIL_COLLAPSED,
    flexShrink: 0,
    background: T.sidebar,
    borderRadius: 16,
    boxShadow: "0 6px 20px rgba(27,34,42,.09)",
    margin: 12,
    display: "flex",
    flexDirection: "column",
    height: "calc(100vh - 24px)",
    overflowX: "hidden",
    transition: "width .18s ease",
    fontFamily: MENU_FONT,
  },
  railOpen: { width: RAIL_OPEN },
  // hover com o menu recolhido: abre POR CIMA do conteúdo (não empurra a tela)
  railHolder: { position: "relative", flexShrink: 0, height: "100%" },
  railPeek: { position: "absolute", top: 0, left: 0, zIndex: 1250, boxShadow: "0 12px 36px rgba(27,34,42,.18)" },

  // cabeçalho da marca (logo + botão de recolher/expandir)
  brandRow: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 34,
    padding: "0 8px",
    margin: "24px 8px 14px",
  },
  // rodapé do rail: recolher/expandir. marginTop auto empurra para o fim tanto no
  // estado aberto (depois do `scroll`, que é flex:1) quanto no recolhido.
  railFoot: {
    marginTop: "auto",
    flexShrink: 0,
    display: "flex",
    justifyContent: "flex-end",
    padding: "6px 10px 12px",
  },
  railFootCollapsed: { justifyContent: "center", padding: "6px 0 12px" },
  footToggle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: T.ink3,
    cursor: "pointer",
    padding: 6,
    borderRadius: 8,
    "& svg": { fontSize: 20, display: "block" },
    "&:hover": { background: T.hover, color: T.ink },
  },
  footToggleFlip: { display: "flex", transform: "rotate(180deg)" },
  // ---- franquia do plano no rodapé (estilo Pipedream) ----
  quota: { flexShrink: 0, borderTop: `1px solid ${T.line}`, padding: "8px 12px 7px", margin: "0 8px" },
  quotaHead: { display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 7 },
  quotaTitle: { fontSize: 10, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: T.ink3 },
  quotaReset: { fontSize: 9.5, color: T.ink3, fontStyle: "italic" },
  // cada franquia em UMA linha: rótulo · barra fina · valor
  quotaRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 6, "&:last-child": { marginBottom: 0 } },
  quotaLabel: { fontSize: 11, color: T.ink2, width: 58, flexShrink: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  quotaTrack: { flex: 1, height: 4, borderRadius: 999, background: T.chip, overflow: "hidden" },
  quotaFill: { display: "block", height: "100%", borderRadius: 999, transition: "width .3s ease" },
  quotaVal: { fontSize: 9.5, color: T.ink3, fontFamily: "ui-monospace, monospace", flexShrink: 0, minWidth: 40, textAlign: "right" },
  quotaMore: { marginTop: 7, fontSize: 11, fontWeight: 500, color: T.ink3, textAlign: "right", cursor: "pointer", "&:hover": { color: T.ink, textDecoration: "underline" } },
  brandCollapsed: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: 30,
    cursor: "pointer",
    "&:hover $brandMark": { opacity: 0 },
    "&:hover $brandHoverIcon": { opacity: 1, color: T.ink },
  },
  brandHoverIcon: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: T.ink3,
    opacity: 0,
    transition: "opacity .12s",
    "& svg": { fontSize: 20 },
  },
  // logo = elemento de UI (o mais LEVE dos três: canvas > título/ações > logo).
  // Altura na escala do menu/título, não outdoor. Ver handoff "logo no app".
  brandMark: { height: 24, width: "auto", display: "block", flexShrink: 0, transition: "opacity .12s" },
  brandLogo: { height: 26, width: "auto", display: "block" },

  // ---- área rolável do menu ----
  scroll: { flex: 1, overflowY: "auto", minHeight: 0, display: "flex", flexDirection: "column" },

  // ---- menu (Nova conversa + grupos de produto) ----
  menu: { padding: "0 8px", display: "flex", flexDirection: "column", gap: 2, flexShrink: 0 },
  rbtn: {
    height: 36,
    borderRadius: 9,
    display: "flex",
    alignItems: "center",
    gap: 11,
    padding: "0 10px",
    color: T.ink2,
    cursor: "pointer",
    position: "relative",
    flexShrink: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
    "& svg": { fontSize: 20, flexShrink: 0, color: T.ink3 },
    "&:hover": { background: T.hover, color: T.ink },
    "&:hover svg": { color: T.ink2 },
  },
  rbtnOn: {
    background: T.activeBg,
    color: T.ink,
    fontWeight: 600,
    boxShadow: `inset 2.55px 0 0 0 ${T.activeAccent}`,
    "& svg": { color: T.ink },
  },
  rlabel: { fontFamily: MENU_FONT, fontSize: 13.5, fontWeight: "inherit", letterSpacing: "-0.006em", color: "inherit" },
  // badge de contagem de alertas — ~60% do padrão do MUI e puxado p/ dentro p/ não cortar.
  alertBadge: { height: 13, minWidth: 13, fontSize: 8.5, fontWeight: 700, lineHeight: "13px", padding: "0 3px", borderRadius: 7, transform: "scale(1) translate(45%, -35%)" },

  // grupo (pai expansível) + filhos indentados
  groupHead: {
    height: 34,
    borderRadius: 9,
    display: "flex",
    alignItems: "center",
    gap: 11,
    padding: "0 10px",
    marginTop: 6,
    color: T.ink2,
    cursor: "pointer",
    userSelect: "none",
    whiteSpace: "nowrap",
    overflow: "hidden",
    "& > svg": { fontSize: 19, flexShrink: 0, color: T.ink3 },
    "&:hover": { background: T.hover, color: T.ink },
  },
  groupLabel: { flex: 1, minWidth: 0, fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em", overflow: "hidden", textOverflow: "ellipsis" },
  groupChevron: { fontSize: 18, flexShrink: 0, color: T.ink3, transition: "transform .15s ease" },
  groupChevronClosed: { transform: "rotate(-90deg)" },
  childBtn: { marginLeft: 22 }, // recolhe o sub-item (pill de seleção mais estreito que o grupo pai)

  // ---- Conversas recentes (copiloto) ----
  sectionLabel: {
    fontSize: 9.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: ".06em",
    color: T.ink3,
    padding: "16px 16px 5px",
    whiteSpace: "nowrap",
    userSelect: "none",
    flexShrink: 0,
  },
  convs: { flex: 1, overflowY: "auto", padding: "0 8px 10px", minHeight: 0 },
  cv: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "7px 10px",
    borderRadius: 9,
    fontSize: 12.5,
    color: T.ink2,
    cursor: "pointer",
    whiteSpace: "nowrap",
    overflow: "hidden",
    position: "relative",
    "&:hover": { background: T.hover },
    "&:hover $cvDel": { opacity: 1 },
    "& > svg": { fontSize: 16, color: T.ink3, flexShrink: 0 },
  },
  cvOn: {
    background: T.activeBg,
    color: T.ink,
    fontWeight: 600,
    boxShadow: `inset 2.55px 0 0 0 ${T.activeAccent}`,
    "& > svg": { color: T.ink },
  },
  cvTitle: { flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" },
  cvDel: {
    opacity: 0,
    color: T.ink3,
    padding: 2,
    transition: "opacity .12s",
    "&:hover": { color: "#C0453B" },
    "& svg": { fontSize: 16 },
  },
  convEmpty: { padding: "8px 16px", fontSize: 11, color: T.ink3, whiteSpace: "nowrap" },
  // skeleton de carregamento das conversas recentes
  "@keyframes nbSkel": { "0%": { opacity: 0.55 }, "50%": { opacity: 1 }, "100%": { opacity: 0.55 } },
  cvSkel: { display: "flex", alignItems: "center", gap: 10, padding: "7px 10px" },
  cvSkelDot: { width: 18, height: 18, borderRadius: 5, background: T.chip, flexShrink: 0, animation: "$nbSkel 1.2s ease-in-out infinite" },
  cvSkelBar: { height: 10, borderRadius: 5, background: T.chip, animation: "$nbSkel 1.2s ease-in-out infinite" },

  // ---- estado recolhido: navegação só com ícones ----
  collapsedNav: { display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "2px 0" },
  cIcon: {
    width: 38,
    height: 38,
    borderRadius: 9,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: T.ink3,
    cursor: "pointer",
    position: "relative",
    "& svg": { fontSize: 20 },
    "&:hover": { background: T.hover, color: T.ink },
  },
  cIconOn: {
    background: T.activeBg,
    color: T.ink,
    boxShadow: `inset 2.55px 0 0 0 ${T.activeAccent}`,
    "& svg": { color: T.ink },
  },

  // ---- main column (também flutuante: card arredondado sobre o canvas escuro) ----
  maincol: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    height: "calc(100vh - 24px)",
    margin: "12px 12px 12px 0",
    background: T.sidebar,
    borderRadius: 16,
    overflow: "hidden",
    boxShadow: "0 6px 20px rgba(27,34,42,.09)",
  },
  topbar: {
    height: 52,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "0 16px",
    background: T.sidebar,
    borderBottom: `1px solid ${T.line}`,
  },
  grow: { flexGrow: 1 },
  content: { flex: 1, overflow: "auto", background: T.bg, minHeight: 0 },
  // faixa "WhatsApp desconectado" (STU-176): fica até a conexão voltar
  waBar: { display: "flex", alignItems: "center", gap: 10, padding: "8px 20px", background: "#FDECEC", borderBottom: "1px solid #F5C9C9", fontSize: 13, color: T.ink || "#1B222A", flexShrink: 0 },
  waBarDot: { width: 8, height: 8, borderRadius: 999, background: "#D14343", flexShrink: 0 },
  waBarText: { flex: 1, minWidth: 0 },
  waBarBtn: { border: "1px solid #E3B4B4", background: "#fff", color: "#8A2E2E", fontWeight: 600, fontSize: 12.5, borderRadius: 8, padding: "4px 12px", cursor: "pointer", fontFamily: "inherit", "&:hover": { background: "#FFF6F6" } },

  // pílula de saldo — NEUTRA (contorno + valor em destaque). Preenchimento
  // verde/escuro em superfície clara pesa/falha contraste; o realce fica no valor.
  creditChip: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    height: 32,
    padding: "0 12px",
    borderRadius: 999,
    background: "transparent",
    border: `1px solid ${T.line}`,
    color: T.ink, // valor em destaque
    fontSize: 12.5,
    fontWeight: 700,
    whiteSpace: "nowrap",
    "& svg": { fontSize: 17, color: T.ink3 }, // ícone neutro
    "&:hover": { background: T.hover },
  },

  // ---- usuário no header (à direita) ----
  userChip: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    padding: "5px 8px 5px 6px",
    borderRadius: 999,
    cursor: "pointer",
    transition: "background .12s",
    "&:hover": { background: T.hover },
  },
  userAvatar: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    flexShrink: 0,
    background: T.ink,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 700,
  },
  userName: {
    maxWidth: 160,
    fontSize: 12.5,
    fontWeight: 600,
    color: T.ink,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  userChevron: { color: T.ink3, fontSize: 18, flexShrink: 0 },

  // ---- menu de contexto do usuário (claro) ----
  userMenuPaper: {
    minWidth: 232,
    marginTop: 8,
    padding: 6,
    background: "#FFFFFF",
    border: `1px solid ${T.line}`,
    borderRadius: 12,
    boxShadow: "0 14px 38px rgba(27,34,42,.16)",
    color: T.ink,
  },
  userMenuName: { padding: "8px 10px 2px", fontSize: 12.5, fontWeight: 700, color: T.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  userMenuEmail: {
    padding: "0 10px 8px",
    fontSize: 11,
    color: T.ink3,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  userMenuDivider: { height: 1, background: T.line, margin: "4px 2px" },
  userMenuItem: {
    fontSize: 12.5,
    fontWeight: 500,
    color: T.ink,
    borderRadius: 8,
    padding: "8px 10px",
    gap: 11,
    "& svg": { fontSize: 18, color: T.ink3 },
    "&:hover": { background: T.hover },
  },
}));

// chevron de recolher/expandir o menu (fica no RODAPÉ do rail) — aponta para a
// esquerda quando aberto e é girado 180° quando recolhido. Herda fontSize via 1em.
const CollapseIcon = () => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="14.5 5 7.5 12 14.5 19" />
  </svg>
);

// navegação do produto, agrupada (grupo pai expansível + sub-itens)
const productGroups = [
  {
    key: "telefonia",
    icon: <TelephonyIcon />,
    items: [
      { key: "numbers", path: "/numbers", icon: <PhoneIcon />, also: ["/cdrs"] },
      { key: "extensions", path: "/extensions", icon: <ExtensionsIcon /> },
    ],
  },
  {
    key: "builder",
    icon: <BuilderIcon />,
    items: [
      { key: "applications", path: "/applications", icon: <AppsIcon />, also: ["/templates"] },
      { key: "agents", path: "/agents", icon: <AgentsIcon /> },
      { key: "schedules", path: "/schedules", icon: <ScheduleIcon /> },
      { key: "events", path: "/events", icon: <EventsIcon /> },
      { key: "alerts", path: "/alerts", icon: <AlertsIcon /> },
      { key: "vault", path: "/vault", icon: <VaultIcon /> },
    ],
  },
  {
    key: "integrations",
    icon: <IntegrationsIcon />,
    items: [
      { key: "connectors", path: "/connectors", icon: <ConnectorsIcon /> },
      { key: "collectors", path: "/collectors", icon: <CollectorsIcon /> },
      { key: "jobs", path: "/jobs", icon: <JobsIcon /> },
      { key: "datasets", path: "/datasets", icon: <DatasetsIcon /> },
      { key: "data", path: "/data", icon: <DataIcon /> },
      { key: "storage", path: "/storage", icon: <StorageIcon /> },
      { key: "developers", path: "/developers", icon: <DevelopersIcon /> },
    ],
  },
];

// todos os itens achatados (para o estado recolhido, só-ícones)
const flatItems = productGroups.reduce((acc, g) => acc.concat(g.items), []);

const Layout = ({ children }) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { isAuth, user, handleLogout, viewingAs, stopViewingAs } = useContext(AuthContext);
  const { activeProjectId, projects } = useProject();
  const { confirm } = useConfirm();
  const [userAnchor, setUserAnchor] = useState(null);

  // contador de ALERTAS não lidos (badge no item "Alertas"). Atualiza ao montar,
  // ao trocar de rota e a cada 60s.
  const [alertCount, setAlertCount] = useState(0);
  useEffect(() => {
    if (!isAuth) return undefined;
    let alive = true;
    const tick = () => api.get("/alerts/count").then(({ data }) => { if (alive) setAlertCount(data.unread || 0); }).catch(() => undefined);
    tick();
    const t = setInterval(tick, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [isAuth, location.pathname]);

  // ícone do item do menu — com badge de contagem no "Alertas" (pequeno, ~60%).
  const itemIcon = item => (
    item.key === "alerts" && alertCount > 0
      ? <Badge badgeContent={alertCount} color="error" overlap="rectangular" classes={{ badge: classes.alertBadge }}>{item.icon}</Badge>
      : item.icon
  );

  // "Conversas recentes" = FLUXOS recentes (cada montagem no copiloto é um fluxo).
  // Fonte no servidor (aplicações). Clique reabre o Builder; o copiloto restaura
  // a conversa daquele fluxo. Recarrega ao trocar de projeto e ao mudar de rota
  // (pra refletir um build recém-criado).
  const [recentFlows, setRecentFlows] = useState([]);
  const [loadingFlows, setLoadingFlows] = useState(true);
  const loadRecentFlows = useCallback(async () => {
    // sem projeto ativo ainda: se os projetos já resolveram (sem nenhum), pára o
    // skeleton; senão mantém carregando (aguardando o token/projeto ficarem prontos).
    if (!activeProjectId) {
      setRecentFlows([]);
      if (isAuth && projects.length === 0) setLoadingFlows(false);
      return;
    }
    try {
      const { data } = await api.get("/applications", { params: { projectId: activeProjectId } });
      // inclui AGENTES de voz (também têm copilot); só as ferramentas (tool) ficam
      // de fora, pois são sub-componentes de um agente, não uma "conversa".
      const flows = (data.applications || [])
        .filter(a => a.triggerType !== "tool" && a.triggerType !== "code_job") // script não tem copiloto (vive em /jobs)
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
        .slice(0, 12);
      setRecentFlows(flows);
      setLoadingFlows(false);
    } catch (e) {
      /* mantém o skeleton; re-tenta quando o token/projetos ficarem prontos */
    }
  }, [activeProjectId, projects.length, isAuth]);
  // depende de projects.length: quando os projetos carregam (token pronto), refaz
  useEffect(() => { loadRecentFlows(); }, [loadRecentFlows, location.pathname]);

  // franquia do plano (rodapé do rail) + saldo BR DID (chip do header)
  const [planUsage, setPlanUsage] = useState(null);
  const [balance, setBalance] = useState(null);
  const loadPlanUsage = useCallback(async () => {
    if (!isAuth) return;
    try {
      const [p, b] = await Promise.all([
        api.get("/me/plan").catch(() => ({ data: null })),
        api.get("/me/balance").catch(() => ({ data: null })),
      ]);
      setPlanUsage(p.data);
      setBalance(b.data);
    } catch (e) {
      /* silencioso */
    }
  }, [isAuth]);
  useEffect(() => { loadPlanUsage(); }, [loadPlanUsage, location.pathname]);

  // STU-176: conexões de WhatsApp caídas → faixa no topo até reconectar
  const [waDown, setWaDown] = useState([]);
  const loadWaHealth = useCallback(async () => {
    if (!isAuth) return;
    try {
      const { data } = await api.get("/connectors");
      setWaDown((data.connectors || []).filter(c => { const h = connectorHealth(c); return h && h.alert; }).map(c => ({ ...c, health: connectorHealth(c) })));
    } catch (e) {
      /* silencioso */
    }
  }, [isAuth]);
  useEffect(() => { loadWaHealth(); }, [loadWaHealth, location.pathname]);
  useEffect(() => {
    const t = setInterval(loadWaHealth, 60000);
    return () => clearInterval(t);
  }, [loadWaHealth]);

  const newConversation = useCallback(() => history.push("/"), [history]);

  // estado recolhido/expandido do rail (lembrado entre sessões; padrão expandido)
  const isFlowRoute = p => /^\/applications\/[^/]+/.test(p || "");
  const [open, setOpen] = useState(() => {
    // FLUXO (/applications/:id) entra RECOLHIDO (espaço p/ canvas+copiloto). QUALQUER
    // outra tela entra ABERTA por padrão. (Evita flicker no load direto/refresh.)
    try { return !isFlowRoute(window.location.pathname); } catch (e) { return true; }
  });
  // peek: rail recolhida EXPANDE enquanto o mouse está em cima (qualquer tela) e
  // recolhe ao sair; `open` é a escolha fixa do usuário (botão do rodapé)
  const [peek, setPeek] = useState(false);
  const peekTimer = useRef(null);
  const shown = open || peek;
  const toggleRail = useCallback(() => { setPeek(false); setOpen(prev => !prev); }, []);

  // Recolhe ao ENTRAR no fluxo e ABRE em qualquer outra tela. Só age na TRANSIÇÃO
  // flow↔não-flow (navegar entre telas não-fluxo preserva o estado atual).
  const onFlowPage = isFlowRoute(location.pathname);
  const wasFlowRef = useRef(onFlowPage);
  useEffect(() => {
    if (onFlowPage !== wasFlowRef.current) {
      setOpen(!onFlowPage);
      wasFlowRef.current = onFlowPage;
    }
  }, [onFlowPage]);
  // pequeno atraso na entrada: passar o mouse de raspão não abre o menu
  const onRailEnter = useCallback(() => {
    clearTimeout(peekTimer.current);
    peekTimer.current = setTimeout(() => setPeek(true), 120);
  }, []);
  const onRailLeave = useCallback(() => { clearTimeout(peekTimer.current); setPeek(false); }, []);
  useEffect(() => () => clearTimeout(peekTimer.current), []);

  // grupos fechados (persistidos): padrão todos abertos
  const [closedGroups, setClosedGroups] = useState(() => {
    // sem preferência salva, os grupos-pai (Telefonia/Construtor/Integrações)
    // começam COLAPSADOS; respeita a escolha do usuário depois de interagir.
    const allClosed = () => new Set(productGroups.map(g => g.key));
    try {
      const raw = localStorage.getItem(GROUPS_KEY);
      return raw != null ? new Set(JSON.parse(raw)) : allClosed();
    } catch (e) {
      return allClosed();
    }
  });
  const toggleGroup = useCallback(key => {
    setClosedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try {
        localStorage.setItem(GROUPS_KEY, JSON.stringify([...next]));
      } catch (e) {
        /* ignore */
      }
      return next;
    });
  }, []);

  const onChat = location.pathname === "/" || location.pathname.startsWith("/chat");
  const newChatActive = onChat;
  const currentFlowId = (location.pathname.match(/^\/applications\/(\d+)/) || [])[1];

  const handleDelete = useCallback(
    async (e, f) => {
      e.stopPropagation();
      const ok = await confirm({
        title: "Excluir fluxo?",
        message: `Excluir "${f.name || "este fluxo"}"? Esta ação não pode ser desfeita.`,
        confirmText: "Excluir",
        danger: true,
      });
      if (!ok) return;
      try {
        await api.delete(`/applications/${f.id}`);
        setRecentFlows(list => list.filter(x => x.id !== f.id));
        if (String(currentFlowId) === String(f.id)) history.push("/");
      } catch (err) {
        toastError(err);
      }
    },
    [confirm, currentFlowId, history]
  );

  // unauthenticated: render the routed children (which redirect to /login)
  if (!isAuth) {
    return <>{children}</>;
  }

  const isProductActive = item => {
    // O editor de fluxo vive sob /applications/:id para TODOS os sabores (automação,
    // coletor, ferramenta, agente). Sem isto, editar um coletor/ferramenta acende
    // "Automações". Atribui ao item certo pelo ?kind da URL.
    if (/^\/applications\/[^/]+/.test(location.pathname)) {
      const kind = new URLSearchParams(location.search).get("kind");
      if (kind === "collector") return item.key === "collectors";
      if (kind === "tool" || kind === "agent") return item.key === "agents";
      return item.key === "applications";
    }
    // casa por FRONTEIRA de caminho (igual ou seguido de "/") — senão "/data" acende
    // em "/datasets" (startsWith puro casava o prefixo).
    const paths = [item.path, ...(item.also || [])];
    return paths.some(p => location.pathname === p || location.pathname.startsWith(p + "/"));
  };

  const fullName = (user && user.name && String(user.name).trim()) || "Conta";
  const firstName = fullName.split(/\s+/)[0];
  const userInitial = firstName.charAt(0).toUpperCase();

  // barras de franquia do plano (rodapé do rail) — só o que é franquia do plano.
  const intensity = pct => (pct >= 85 ? "Intenso" : pct >= 50 ? "Moderado" : "Leve");
  const quotaBars = (() => {
    if (!planUsage) return [];
    const limits = planUsage.limits || {};
    const usage = planUsage.usage || {};
    const bars = [];
    const push = (label, used, limit, aiIntensity) => {
      if (limit == null) return; // ilimitado → não mostra
      const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
      bars.push({ label, used, limit, pct, aiIntensity });
    };
    push("IA", usage["ai.tokens"] || 0, limits.aiTokens, true);
    push("Execuções", usage["flow.execution"] || 0, limits.flowExecutionsPerMonth);
    // "Nós" fica só na página interna de Uso (rodapé enxuto)
    push("Agente", usage["agent.voice"] || 0, limits.agentTestMinutes);
    push("WhatsApp", usage["whatsapp.message"] || 0, limits.whatsappTemplates);
    push("SMS", usage["sms.message"] || 0, limits.smsPerMonth);
    return bars;
  })();
  const resetDays =
    planUsage && planUsage.cycle
      ? Math.max(0, Math.ceil((new Date(planUsage.cycle.end).getTime() - Date.now()) / 86400000))
      : null;

  return (
    <div className={clsx(classes.root, viewingAs && classes.rootViewing)}>
      {viewingAs && (
        // barra fixa: quem está olhando precisa lembrar, a cada segundo, que o
        // que vê é a conta de outra pessoa
        <div className={classes.viewingBar}>
          <span className={classes.viewingText}>
            Você está vendo a conta de <b>{viewingAs.name || viewingAs.email}</b> — somente leitura
          </span>
          <button type="button" className={classes.viewingExit} onClick={stopViewingAs}>
            Sair da visualização
          </button>
        </div>
      )}
      <div className={classes.railHolder} style={peek && !open ? { width: RAIL_COLLAPSED + 24 } : undefined}>
      <nav className={clsx(classes.rail, shown && classes.railOpen, peek && !open && classes.railPeek)} onMouseEnter={onRailEnter} onMouseLeave={onRailLeave}>
        {/* marca (o recolher/expandir mora no rodapé do rail) */}
        <div className={classes.brandRow}>
          {shown ? (
            <img src={logoFull} alt="Numera" className={classes.brandLogo} />
          ) : (
            <Tooltip title="Expandir menu" placement="right">
              <div className={classes.brandCollapsed} onClick={toggleRail}>
                <img src={logoMark} alt="Numera" className={classes.brandMark} />
                <span className={classes.brandHoverIcon}>
                  <span className={classes.footToggleFlip}>
                    <CollapseIcon />
                  </span>
                </span>
              </div>
            </Tooltip>
          )}
        </div>

        {shown ? (
          <div className={classes.scroll}>
            {/* copiloto: Nova conversa + navegação agrupada */}
            <div className={classes.menu}>
              <div className={clsx(classes.rbtn, newChatActive && classes.rbtnOn)} onClick={newConversation}>
                <AddIcon />
                <span className={classes.rlabel}>Nova conversa</span>
              </div>

              <div
                className={clsx(classes.rbtn, history.location.pathname === "/recepcao" && classes.rbtnOn)}
                onClick={() => history.push("/recepcao")}
              >
                <StartIcon />
                <span className={classes.rlabel}>Começar</span>
              </div>

              {productGroups.map(group => {
                const groupOpen = !closedGroups.has(group.key);
                return (
                  <React.Fragment key={group.key}>
                    <div className={classes.groupHead} onClick={() => toggleGroup(group.key)}>
                      {group.icon}
                      <span className={classes.groupLabel}>{i18n.t(`menu.groups.${group.key}`)}</span>
                      <ChevronIcon className={clsx(classes.groupChevron, !groupOpen && classes.groupChevronClosed)} />
                    </div>
                    {groupOpen &&
                      group.items.map(item => (
                        <div
                          key={item.key}
                          className={clsx(classes.rbtn, classes.childBtn, isProductActive(item) && classes.rbtnOn)}
                          onClick={() => history.push(item.path)}
                        >
                          {itemIcon(item)}
                          <span className={classes.rlabel}>{i18n.t(`menu.${item.key}`)}</span>
                        </div>
                      ))}
                  </React.Fragment>
                );
              })}
            </div>

            {/* conversas recentes = fluxos recentes (cada montagem é um fluxo) */}
            <div className={classes.sectionLabel}>Conversas recentes</div>
            <div className={classes.convs}>
              {loadingFlows && recentFlows.length === 0 ? (
                [72, 58, 66, 50].map((w, i) => (
                  <div key={i} className={classes.cvSkel}>
                    <span className={classes.cvSkelDot} />
                    <span className={classes.cvSkelBar} style={{ width: `${w}%`, animationDelay: `${i * 0.12}s` }} />
                  </div>
                ))
              ) : recentFlows.length === 0 ? (
                <div className={classes.convEmpty}>Nenhuma conversa ainda.</div>
              ) : (
                recentFlows.map(f => (
                  <div
                    key={f.id}
                    className={clsx(classes.cv, String(currentFlowId) === String(f.id) && classes.cvOn)}
                    onClick={() => history.push({ pathname: f.triggerType === "agent" ? `/agents/${f.id}` : `/applications/${f.id}`, state: { openCopilot: true } })}
                  >
                    <ChatIcon />
                    <span className={classes.cvTitle}>{f.name || "Fluxo sem nome"}</span>
                    <Tooltip title="Excluir">
                      <IconButton size="small" className={classes.cvDel} onClick={e => handleDelete(e, f)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </div>
                ))
              )}
            </div>
          </div>
        ) : (
          // recolhido: navegação só com ícones (Nova conversa + itens do produto)
          <div className={classes.collapsedNav}>
            <Tooltip title="Nova conversa" placement="right">
              <div className={clsx(classes.cIcon, newChatActive && classes.cIconOn)} onClick={newConversation}>
                <AddIcon />
              </div>
            </Tooltip>
            {flatItems.map(item => (
              <Tooltip key={item.key} title={i18n.t(`menu.${item.key}`)} placement="right">
                <div
                  className={clsx(classes.cIcon, isProductActive(item) && classes.cIconOn)}
                  onClick={() => history.push(item.path)}
                >
                  {itemIcon(item)}
                </div>
              </Tooltip>
            ))}
          </div>
        )}

        {/* franquia do plano (estilo Pipedream "Credits Used") — só quando aberto */}
        {shown && quotaBars.length > 0 && (
          <div className={classes.quota}>
            <div className={classes.quotaHead}>
              <span className={classes.quotaTitle}>Franquia do plano</span>
              {resetDays != null && <span className={classes.quotaReset}>renova em {resetDays}d</span>}
            </div>
            {quotaBars.map(b => {
              const color = b.pct >= 100 ? "#C0453B" : b.pct >= 85 ? "#C77A12" : T.activeAccent;
              return (
                <div key={b.label} className={classes.quotaRow}>
                  <span className={classes.quotaLabel}>{b.label}</span>
                  <span className={classes.quotaTrack}>
                    <span className={classes.quotaFill} style={{ width: `${b.pct}%`, background: color }} />
                  </span>
                  <span className={classes.quotaVal}>
                    {b.aiIntensity ? intensity(b.pct) : `${b.used}/${b.limit}`}
                  </span>
                </div>
              );
            })}
            <div className={classes.quotaMore} onClick={() => history.push("/developers/usage")}>Ver mais</div>
          </div>
        )}

        {/* rodapé: recolher/expandir o menu */}
        <div className={clsx(classes.railFoot, !shown && classes.railFootCollapsed)}>
          <Tooltip title={open ? "Recolher menu" : shown ? "Fixar menu aberto" : "Expandir menu"} placement="right">
            <div className={classes.footToggle} onClick={toggleRail}>
              <span className={clsx(!open && classes.footToggleFlip)}>
                <CollapseIcon />
              </span>
            </div>
          </Tooltip>
        </div>
      </nav>
      </div>

      <Menu
        anchorEl={userAnchor}
        open={!!userAnchor}
        onClose={() => setUserAnchor(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ className: classes.userMenuPaper }}
        MenuListProps={{ disablePadding: true }}
      >
        <div className={classes.userMenuName}>{fullName}</div>
        {user && user.email && <div className={classes.userMenuEmail}>{user.email}</div>}
        <div className={classes.userMenuDivider} />
        <MenuItem
          className={classes.userMenuItem}
          onClick={() => {
            setUserAnchor(null);
            history.push("/settings");
          }}
        >
          <SettingsIcon fontSize="small" />
          Configurações
        </MenuItem>
        <MenuItem
          className={classes.userMenuItem}
          onClick={() => {
            setUserAnchor(null);
            history.push("/plans");
          }}
        >
          <PlansIcon fontSize="small" />
          Planos
        </MenuItem>
        <MenuItem
          className={classes.userMenuItem}
          onClick={() => {
            setUserAnchor(null);
            history.push("/billing");
          }}
        >
          <BillingIcon fontSize="small" />
          Assinatura e faturas
        </MenuItem>
        {user && user.isStaff && (
          <MenuItem
            className={classes.userMenuItem}
            onClick={() => {
              setUserAnchor(null);
              history.push("/admin");
            }}
          >
            <AdminIcon fontSize="small" />
            Backoffice
          </MenuItem>
        )}
        <MenuItem
          className={classes.userMenuItem}
          onClick={() => {
            setUserAnchor(null);
            handleLogout();
          }}
        >
          <ExitIcon fontSize="small" />
          Sair
        </MenuItem>
      </Menu>

      <div className={classes.maincol}>
        <header className={classes.topbar}>
          <ProjectSwitcher />
          <div className={classes.grow} />
          <WebPhoneChip />
          <Tooltip title={balance && balance.configured ? "Saldo pré-pago (BR DID) — clique para detalhes" : "Conecte a BR DID para ver o saldo"}>
            <div className={classes.creditChip} onClick={() => history.push("/developers/usage")} style={{ cursor: "pointer" }}>
              <WalletIcon />
              {balance && balance.configured && balance.saldo != null
                ? `R$ ${Number(balance.saldo).toFixed(2).replace(".", ",")}`
                : "Saldo"}
            </div>
          </Tooltip>
          <div className={classes.userChip} onClick={e => setUserAnchor(e.currentTarget)}>
            <div className={classes.userAvatar}>{userInitial}</div>
            <span className={classes.userName}>{firstName}</span>
            <ChevronIcon className={classes.userChevron} />
          </div>
        </header>
        {waDown.length > 0 && (
          <div className={classes.waBar}>
            <span className={classes.waBarDot} />
            <span className={classes.waBarText}>
              {waDown.length === 1
                ? <><b>{waDown[0].name}</b> está {waDown[0].health.label} — as mensagens do WhatsApp não estão chegando.</>
                : <>{waDown.length} conexões de WhatsApp fora do ar ({waDown.map(c => c.name).join(", ")}) — as mensagens não estão chegando.</>}
            </span>
            <button type="button" className={classes.waBarBtn} onClick={() => history.push(waDown.length === 1 ? `/connectors?reconnect=${waDown[0].id}` : "/connectors")}>
              Reconectar
            </button>
          </div>
        )}
        <main className={classes.content}>{children}</main>
      </div>
    </div>
  );
};

export default Layout;
